import { supabase } from "../lib/supabase";
import { getCurrentPlatform } from "../utils/platform";
import { fetchAllRows, summarizeCurrencyAmounts } from "../utils/reporting.js";
import { teamService } from "./partnerService";
import { createAdminReportingQueries } from "./adminReportingQueries.js";
import { getCountableEnrollmentLessons } from "../utils/enrollmentLessons";
import {
  buildMonthlyTrends,
  summarizeAdminAnalytics,
} from "../utils/adminAnalytics.js";
import {
  excludeAnalyticsTransactions,
  excludeAnalyticsUsers,
  getAnalyticsTestUserIds,
  getAnalyticsTransactionExclusionIds,
} from "./analyticsFilterService";

const adminReporting = createAdminReportingQueries(supabase, {
  async getExclusions(options) {
    const [userIds, transactions] = await Promise.all([
      getAnalyticsTestUserIds(options), getAnalyticsTransactionExclusionIds(),
    ]);
    return { userIds, ...transactions };
  },
  getSales: (options) => teamService.getAllTransactions(options),
});

export const analyticsService = {
  // Admin analytics (/admin/analytics). Every source is paged with
  // fetchAllRows; payments are not inner-joined to courses because cart
  // checkouts store course_id = NULL. Revenue stays per currency.
  async getAdminAnalytics(timeframe = "30", options = {}) {
    try {
      const [excludedUserIds, transactionExclusions] = await Promise.all([
        getAnalyticsTestUserIds(options),
        getAnalyticsTransactionExclusionIds(),
      ]);
      const days = parseInt(timeframe);
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - days);
      const priorStart = new Date(startDate);
      priorStart.setDate(priorStart.getDate() - days);
      const platform = getCurrentPlatform();

      const [users, enrollments, payments, progress, monthlyTrends, realTime] =
        await Promise.all([
          fetchAllRows(() =>
            excludeAnalyticsUsers(
              supabase
                .from("user_profiles")
                .select("id, role, created_at, is_active, account_type, country"),
              excludedUserIds,
              "id",
            ),
          ),
          fetchAllRows(() =>
            excludeAnalyticsTransactions(
              excludeAnalyticsUsers(
                supabase
                  .from("course_enrollments")
                  .select(
                    "id, user_id, course_id, enrollment_status, enrolled_at, completed_at, progress_percentage, courses(id, title, category, iso_standard)",
                  )
                  .eq("platform", platform)
                  .gte("enrolled_at", startDate.toISOString()),
                excludedUserIds,
              ),
              transactionExclusions.courseEnrollmentIds,
            ),
          ),
          // The prior window is fetched too, for like-for-like growth.
          fetchAllRows(() =>
            excludeAnalyticsTransactions(
              excludeAnalyticsUsers(
                supabase
                  .from("payments")
                  .select(
                    "id, user_id, course_id, amount, currency, payment_status, payment_method, paid_at, created_at, course:courses(id, title, category, iso_standard)",
                  )
                  .eq("platform", platform)
                  .eq("payment_status", "completed")
                  .gte("created_at", priorStart.toISOString()),
                excludedUserIds,
              ),
              transactionExclusions.paymentIds,
            ),
          ),
          fetchAllRows(() =>
            excludeAnalyticsTransactions(
              excludeAnalyticsUsers(
                supabase
                  .from("lesson_progress")
                  .select(
                    "id, user_id, time_spent_minutes, last_accessed_at, completed_at",
                  )
                  .gte("last_accessed_at", startDate.toISOString()),
                excludedUserIds,
              ),
              transactionExclusions.courseEnrollmentIds,
              "enrollment_id",
            ),
          ),
          this.getMonthlyTrends(options),
          this.getRealTimeMetrics(options),
        ]);

      return {
        ...summarizeAdminAnalytics({
          users,
          enrollments,
          payments,
          progress,
          start: startDate,
          priorStart,
          now,
        }),
        timeframe_days: days,
        monthly_trends: monthlyTrends,
        real_time: realTime,
      };
    } catch (error) {
      console.error("Error fetching admin analytics:", error);
      throw error;
    }
  },

  // Six calendar months of revenue (per currency), signups and enrollments.
  async getMonthlyTrends(options = {}) {
    const [excludedUserIds, transactionExclusions] = await Promise.all([
      getAnalyticsTestUserIds(options),
      getAnalyticsTransactionExclusionIds(),
    ]);
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const platform = getCurrentPlatform();

    const [payments, users, enrollments] = await Promise.all([
      fetchAllRows(() =>
        excludeAnalyticsTransactions(
          excludeAnalyticsUsers(
            supabase
              .from("payments")
              .select("id, amount, currency, payment_status, paid_at, created_at")
              .eq("platform", platform)
              .eq("payment_status", "completed")
              .gte("paid_at", startDate.toISOString())
              .lt("paid_at", endDate.toISOString()),
            excludedUserIds,
          ),
          transactionExclusions.paymentIds,
        ),
      ),
      fetchAllRows(() =>
        excludeAnalyticsUsers(
          supabase
            .from("user_profiles")
            .select("id, created_at")
            .gte("created_at", startDate.toISOString())
            .lt("created_at", endDate.toISOString()),
          excludedUserIds,
          "id",
        ),
      ),
      fetchAllRows(() =>
        excludeAnalyticsTransactions(
          excludeAnalyticsUsers(
            supabase
              .from("course_enrollments")
              .select("id, enrollment_status, enrolled_at")
              .eq("platform", platform)
              .gte("enrolled_at", startDate.toISOString())
              .lt("enrolled_at", endDate.toISOString()),
            excludedUserIds,
          ),
          transactionExclusions.courseEnrollmentIds,
        ),
      ),
    ]);

    return buildMonthlyTrends({ payments, users, enrollments, now });
  },

  // Get course management analytics for admin
  async getCourseManagementAnalytics(options = {}) {
    try {
      const [excludedUserIds, transactionExclusions] = await Promise.all([
        getAnalyticsTestUserIds(options),
        getAnalyticsTransactionExclusionIds(),
      ]);
      const { data: courses, error: coursesError } = await supabase?.from(
        "courses",
      )?.select(`
          id,
          title,
          category,
          iso_standard,
          price,
          is_active,
          created_at,
          instructor_id,
          user_profiles!courses_instructor_id_fkey(full_name, email)
        `);

      if (coursesError) throw coursesError;

      // Get enrollment counts for each course
      const courseIds = courses?.map((c) => c?.id) || [];
      let enrollmentQuery = supabase
        ?.from("course_enrollments")
        ?.select("course_id, enrollment_status")
        .eq("platform", getCurrentPlatform())
        ?.in("course_id", courseIds);
      enrollmentQuery = excludeAnalyticsUsers(enrollmentQuery, excludedUserIds);
      enrollmentQuery = excludeAnalyticsTransactions(
        enrollmentQuery,
        transactionExclusions.courseEnrollmentIds,
      );
      const { data: enrollmentCounts, error: enrollmentError } =
        await enrollmentQuery;

      if (enrollmentError) throw enrollmentError;

      let revenueQuery = supabase
        ?.from("payments")
        ?.select("course_id, amount, payment_status")
        .eq("platform", getCurrentPlatform())
        ?.in("course_id", courseIds)
        ?.eq("payment_status", "completed");
      revenueQuery = excludeAnalyticsUsers(revenueQuery, excludedUserIds);
      revenueQuery = excludeAnalyticsTransactions(
        revenueQuery,
        transactionExclusions.paymentIds,
      );
      const { data: courseRevenue, error: revenueError } =
        await revenueQuery;

      if (revenueError) throw revenueError;

      // Combine data
      const coursesWithAnalytics = courses?.map((course) => {
        const enrollments =
          enrollmentCounts?.filter((e) => e?.course_id === course?.id) || [];
        const revenue =
          courseRevenue
            ?.filter((r) => r?.course_id === course?.id)
            ?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0) || 0;
        const completions =
          enrollments?.filter((e) => e?.enrollment_status === "completed")
            ?.length || 0;
        const totalEnrollments = enrollments?.length;

        return {
          ...course,
          total_enrollments: totalEnrollments,
          completed_enrollments: completions,
          completion_rate:
            totalEnrollments > 0
              ? Math.round((completions / totalEnrollments) * 100)
              : 0,
          revenue: Math.round(revenue),
          instructor_name: course?.user_profiles?.full_name || "Unknown",
          instructor_email: course?.user_profiles?.email || "",
        };
      });

      return coursesWithAnalytics?.sort(
        (a, b) => b?.total_enrollments - a?.total_enrollments,
      );
    } catch (error) {
      console.error("Error fetching course management analytics:", error);
      return [];
    }
  },

  // Get user management analytics
  async getUserManagementAnalytics(options = {}) {
    return adminReporting.getUsers(options);
  },

  async getReportingOverview(options = {}) {
    return adminReporting.getOverview(options);
  },

  async getReportingEnrollments(options = {}) {
    return adminReporting.getEnrollments(options);
  },

  // Get comprehensive student analytics
  async getStudentAnalytics(timeframe = "30", options = {}) {
    const data = await adminReporting.getStudents(timeframe, options);
    return {
      ...data,
      recent_activities: data.recent_activities.map((activity) => ({
        ...activity, time_ago: this.getTimeAgo(activity.at),
      })),
    };
  },

  // Get paginated activity history for history view
  async getActivityHistory({ page = 1, limit = 20, search = "", action = "all", excludeTestAccounts = true, testAccountEmails } = {}) {
    const rows = await adminReporting.getEnrollments({ excludeTestAccounts, testAccountEmails });
    const needle = search.toLowerCase();
    const activities = rows.filter((e) => {
      if (action === "completed" && e.enrollment_status !== "completed") return false;
      if (action === "enrolled" && e.enrollment_status !== "active") return false;
      return !needle || (e.user?.full_name || "").toLowerCase().includes(needle)
        || (e.course?.title || "").toLowerCase().includes(needle);
    }).map((e) => {
      const at = e.enrollment_status === "completed" ? e.completed_at || e.enrolled_at : e.enrolled_at;
      return {
        id: e.id, student_name: e.user?.full_name || "Student",
        action: e.enrollment_status === "completed" ? "completed" : "enrolled in",
        course_name: e.course?.title || "Course", enrolled_at: at, time_ago: this.getTimeAgo(at),
      };
    }).sort((a,b) => new Date(b.enrolled_at) - new Date(a.enrolled_at));
    return { activities: activities.slice((page - 1) * limit, page * limit), total: activities.length, page, totalPages: Math.max(1, Math.ceil(activities.length / limit)) };
  },

  // Get detailed student progress for admin view
  async getStudentDetailedProgress({
    page = 1,
    limit = 10,
    search = "",
    excludeTestAccounts = true,
    testAccountEmails,
  }) {
    try {
      const [excludedUserIds, transactionExclusions] = await Promise.all([
        getAnalyticsTestUserIds({
          excludeTestAccounts,
          testAccountEmails,
        }),
        getAnalyticsTransactionExclusionIds(),
      ]);
      const start = (page - 1) * limit;
      const end = start + limit - 1;

      // Base query for enrollments
      // Changed !inner to standard join (left join) to ensure we get enrollments even if relationships have issues
      let query = supabase
        .from("course_enrollments")
        .select(
          `
          id,
          user_id,
          enrolled_at,
          progress_percentage,
          enrollment_status,
          user:user_profiles!course_enrollments_user_id_fkey(full_name, email),
          delivery_mode,
          accreditation_type,
          course:courses(title, id)
        `,
          { count: "exact" },
        )
        .eq("platform", getCurrentPlatform());
      query = excludeAnalyticsUsers(query, excludedUserIds);
      query = excludeAnalyticsTransactions(
        query,
        transactionExclusions.courseEnrollmentIds,
      );

      // Apply search if provided
      if (search) {
        // Fallback to client-side filtering if search is active since simple join search is tricky
        // But for basic name search on joined table:
        // query = query.ilike('user.full_name', `%${search}%`); // This often fails on foreign tables in JS client without specific embedding setup
        // relying on memory filtering for now as implemented below
      }

      // Sort by enrolled_at since last_accessed_at does not exist on this table
      query = query
        .order("enrolled_at", { ascending: false })
        .range(start, end);

      const { data: enrollments, error, count } = await query;

      if (error) {
        console.error("Supabase error fetching enrollments:", error);
        throw error;
      }

      if (!enrollments || enrollments.length === 0) {
        return { data: [], total: 0, page: 1, totalPages: 1 };
      }

      // Batch fetch lesson counts and progress for all enrollments (no per-row queries)
      const courseIds = [
        ...new Set(enrollments.map((e) => e.course?.id).filter(Boolean)),
      ];
      const enrollmentIds = enrollments.map((e) => e.id);

      // 1. Batch: Get lesson counts per course
      const { data: allLessons } = await supabase
        .from("lessons")
        .select("id, course_id, type, is_published, delivery_mode")
        .in("course_id", courseIds);

      const lessonsByCourse = new Map();
      (allLessons || []).forEach((l) => {
        if (!lessonsByCourse.has(l.course_id)) {
          lessonsByCourse.set(l.course_id, []);
        }
        lessonsByCourse.get(l.course_id).push(l);
      });

      // 2. Batch: Get lesson_progress for all enrollments
      const { data: allProgress } = await supabase
        .from("lesson_progress")
        .select("enrollment_id, lesson_id, is_completed, last_accessed_at")
        .in("enrollment_id", enrollmentIds);

      const progressByEnrollment = new Map();
      (allProgress || []).forEach((p) => {
        if (!progressByEnrollment.has(p.enrollment_id)) {
          progressByEnrollment.set(p.enrollment_id, []);
        }
        progressByEnrollment.get(p.enrollment_id).push(p);
      });

      // Map using pre-fetched data (no per-enrollment queries)
      const enhancedEnrollments = enrollments.map((enrollment) => {
        const countableLessons = getCountableEnrollmentLessons(
          lessonsByCourse.get(enrollment.course?.id) || [],
          enrollment,
        );
        const countableLessonIds = new Set(
          countableLessons.map((lesson) => lesson.id),
        );
        const totalLessons = countableLessons.length;
        const progressEntries = progressByEnrollment.get(enrollment.id) || [];
        const completedLessons = progressEntries.filter(
          (p) => p.is_completed && countableLessonIds.has(p.lesson_id),
        ).length;

        const latestAccess = progressEntries.reduce((latest, p) => {
          const d = new Date(p.last_accessed_at);
          return d > latest ? d : latest;
        }, new Date(0));

        const realLastActive =
          latestAccess.getTime() > 0
            ? latestAccess.toISOString()
            : enrollment.enrolled_at;

        return {
          id: enrollment.id,
          userId: enrollment.user_id,
          studentName:
            enrollment.user?.full_name ||
            enrollment.user?.email ||
            "Unknown Student",
          studentEmail: enrollment.user?.email,
          courseTitle: enrollment.course?.title,
          progress: enrollment.progress_percentage || 0,
          completedModules: completedLessons,
          totalModules: totalLessons,
          lastActive: realLastActive,
          status: enrollment.enrollment_status,
        };
      });

      // Filter in memory if search was generic (since cross-table ilike is hard)
      // If we need true server-side search on joined tables, we'd add it here.
      let filteredData = enhancedEnrollments;
      if (search) {
        const lowerSearch = search.toLowerCase();
        filteredData = enhancedEnrollments.filter(
          (item) =>
            item.studentName.toLowerCase().includes(lowerSearch) ||
            item.courseTitle.toLowerCase().includes(lowerSearch),
        );
      }

      return {
        data: filteredData,
        total: count || 0,
        page,
        totalPages: Math.ceil((count || 0) / limit),
      };
    } catch (error) {
      console.error("Error in getStudentDetailedProgress:", error);
      return { data: [], total: 0, page: 1, totalPages: 1 };
    }
  },

  // Get top performing courses
  async getTopCourses(limit = 10, options = {}) {
    return adminReporting.getTopCourses(limit, options);
  },

  // Get enrollment trends
  async getEnrollmentTrends(period = "30", options = {}) {
    try {
      const [excludedUserIds, transactionExclusions] = await Promise.all([
        getAnalyticsTestUserIds(options),
        getAnalyticsTransactionExclusionIds(),
      ]);
      const days = parseInt(period);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      let query = supabase
        ?.from("course_enrollments")
        ?.select("enrolled_at, enrollment_status")
        ?.gte("enrolled_at", startDate?.toISOString());
      query = excludeAnalyticsUsers(query, excludedUserIds);
      query = excludeAnalyticsTransactions(
        query,
        transactionExclusions.courseEnrollmentIds,
      );
      query = query.order("enrolled_at", { ascending: true });
      const { data, error } = await query;

      if (error) throw error;

      // Group enrollments by date
      const trendData = {};
      data?.forEach((enrollment) => {
        const date = new Date(enrollment?.enrolled_at)?.toDateString();
        if (!trendData?.[date]) {
          trendData[date] = { total: 0, active: 0, completed: 0 };
        }
        trendData[date].total++;
        if (enrollment?.enrollment_status === "active")
          trendData[date].active++;
        if (enrollment?.enrollment_status === "completed")
          trendData[date].completed++;
      });

      return Object?.entries(trendData)?.map(([date, counts]) => ({
        date,
        ...counts,
      }));
    } catch (error) {
      console.error("Error fetching enrollment trends:", error);
      return [];
    }
  },

  // Get activity snapshot for a rolling window with vs-prior-period deltas.
  // period: "24h" | "7d" | "30d" | "90d"
  async getActivityMetrics(period = "24h", options = {}) {
    const now = new Date();
    const nowMs = now.getTime();
    const isAllTime = period === "all";
    const days = { "24h": 1, "7d": 7, "30d": 30, "90d": 90 }[period] || 1;
    const windowMs = days * 24 * 60 * 60 * 1000;
    const periodStart = isAllTime ? 0 : nowMs - windowMs;
    const priorStart = isAllTime ? 0 : nowMs - 2 * windowMs;
    const excludedUsers = new Set(await getAnalyticsTestUserIds(options));
    const excludedEmails = new Set((options.testAccountEmails || []).map((email) => email.toLowerCase()));
    const [rawEnrollments, allSales, profiles, progress, partnerInstructors, approvals, batches] = await Promise.all([
      adminReporting.getEnrollments(options),
      teamService.getAllTransactions(options),
      fetchAllRows(() => supabase.from("user_profiles").select("id, created_at")),
      fetchAllRows(() => supabase.from("lesson_progress").select("id, user_id, enrollment_id, last_accessed_at")),
      fetchAllRows(() => supabase.from("partner_instructors").select(`
        id, instructor_id, status, created_at, invited_email,
        partner:partners(id, name, slug),
        instructor:instructor_profiles(id, user:user_profiles!instructor_profiles_id_fkey(full_name, email))
      `)),
      fetchAllRows(() => supabase.from("instructor_profiles").select(`
        id, approval_status, approved_at, average_rating,
        user:user_profiles!instructor_profiles_id_fkey(full_name, email)
      `).eq("approval_status", "approved")),
      fetchAllRows(() => supabase.from("training_batches").select(`
        id, title, status, published_at, start_date, instructor_id,
        instructor:user_profiles!training_batches_instructor_id_fkey(id, full_name, email),
        course:courses(id, title)
      `)),
    ]);
    const enrollments = rawEnrollments.filter((row) =>
      ["active", "completed"].includes(row.enrollment_status) && !excludedUsers.has(row.user_id));
    const sales = allSales.filter((row) => row.status === "completed" && !excludedUsers.has(row.user_id));
    const eligibleEnrollments = new Map(enrollments.map((row) => [row.id, row.user_id]));
    const signups = profiles.filter((row) => !excludedUsers.has(row.id));
    const grants = sales.filter((row) => row.sale_type === "direct" && row.payment_method === "admin_assignment");
    const grantIds = new Set(grants.map((row) => row.id));
    const saleDate = (row) => row.sale_type === "direct" ? row.paid_at || row.created_at : row.created_at;
    const inWindow = (date, start, end, includeEnd = false) => {
      if (!date) return false;
      const time = new Date(date).getTime();
      return time >= start && (includeEnd ? time <= end : time < end);
    };
    const currentRows = (rows, dateKey) => rows.filter((row) => inWindow(
      typeof dateKey === "function" ? dateKey(row) : row[dateKey], periodStart, nowMs, true));
    const summarize = (start, end, includeEnd) => {
      const within = (date) => inWindow(date, start, end, includeEnd);
      const enrolled = enrollments.filter((row) => within(row.enrolled_at));
      const completed = enrollments.filter((row) => row.enrollment_status === "completed" && within(row.completed_at));
      const paid = sales.filter((row) => within(saleDate(row)));
      const direct = paid.filter((row) => row.sale_type === "direct");
      const partner = paid.filter((row) => row.sale_type === "partner");
      return {
        direct_enrollments: enrolled.filter((row) => !row.partner_id).length,
        partner_enrollments: enrolled.filter((row) => row.partner_id).length,
        direct_completions: completed.filter((row) => !row.partner_id).length,
        partner_completions: completed.filter((row) => row.partner_id).length,
        // Admin completions are a labeled subset of the direct/partner completion counts.
        admin_completions: completed.filter((row) => grantIds.has(row.payment_id)).length,
        signups: signups.filter((row) => within(row.created_at)).length,
        direct_revenue_by_currency: summarizeCurrencyAmounts(direct, "total_amount"),
        partner_revenue_by_currency: summarizeCurrencyAmounts(partner, "total_amount"),
        direct_payments: direct.length,
        partner_sales: partner.length,
        admin_grants: grants.filter((row) => within(saleDate(row))).length,
      };
    };
    const latest = (rows, key) => currentRows(rows, key).sort((a, b) =>
      new Date(typeof key === "function" ? key(b) : b[key]) - new Date(typeof key === "function" ? key(a) : a[key])
      || String(a.id).localeCompare(String(b.id))).slice(0, 8);
    const enrollmentFeed = (row, at) => ({
      id: row.id, full_name: row.user?.full_name || "Unknown", email: row.user?.email || "",
      course_id: row.course?.id || row.course_id, course_title: row.course?.title || "Untitled",
      partner_id: row.partner_id || null, partner_name: row.partner?.name || null,
      partner_slug: row.partner?.slug, at,
    });
    const delivered = new Map();
    for (const batch of batches) {
      if (batch.status === "completed") delivered.set(batch.instructor_id, (delivered.get(batch.instructor_id) || 0) + 1);
    }
    return {
      as_of: now.toISOString(), is_all_time: isAllTime,
      current: {
        ...summarize(periodStart, nowMs, true),
        active_learners: new Set(progress.filter((row) =>
          row.user_id && eligibleEnrollments.get(row.enrollment_id) === row.user_id
          && inWindow(row.last_accessed_at, nowMs - 24 * 60 * 60 * 1000, nowMs, true))
          .map((row) => row.user_id)).size,
      },
      prior: summarize(priorStart, periodStart, false),
      recent_direct_enrollments: latest(enrollments.filter((row) => !row.partner_id), "enrolled_at")
        .map((row) => enrollmentFeed(row, row.enrolled_at)),
      recent_partner_enrollments: latest(enrollments.filter((row) => row.partner_id), "enrolled_at")
        .map((row) => enrollmentFeed(row, row.enrolled_at)),
      recent_completions: latest(enrollments.filter((row) => row.enrollment_status === "completed"), "completed_at")
        .map((row) => enrollmentFeed(row, row.completed_at)),
      recent_partner_instructors: latest(partnerInstructors.filter((row) =>
        !excludedUsers.has(row.instructor_id) && !excludedEmails.has(row.invited_email?.toLowerCase())), "created_at").map((row) => ({
        id: row.id, status: row.status,
        full_name: row.instructor?.user?.full_name || row.invited_email || "Pending invite",
        email: row.instructor?.user?.email || row.invited_email || "",
        partner_id: row.partner?.id, partner_name: row.partner?.name || "Unknown partner",
        partner_slug: row.partner?.slug, at: row.created_at,
      })),
      recent_partner_sales: latest(sales.filter((row) => row.sale_type === "partner"), saleDate).map((row) => ({
        id: row.id, amount: Number(row.total_amount || 0), currency: row.currency,
        currency_source: row.currency_source,
        full_name: row.user?.full_name || "Unknown", email: row.user?.email || "",
        partner_id: row.partner_id, partner_name: row.team?.name || "Unknown partner",
        partner_slug: row.team?.slug, course_id: row.course_id,
        course_title: row.course?.title || "Untitled", at: saleDate(row),
      })),
      recent_instructor_approvals: latest(approvals.filter((row) => !excludedUsers.has(row.id)), "approved_at").map((row) => ({
        id: row.id, full_name: row.user?.full_name || "Unknown", email: row.user?.email || "",
        rating: Number(row.average_rating || 0), batches_delivered: delivered.get(row.id) || 0,
        at: row.approved_at,
      })),
      recent_batches: latest(batches.filter((row) => !excludedUsers.has(row.instructor_id)), "published_at").map((row) => ({
        id: row.id, title: row.title, status: row.status, start_date: row.start_date,
        instructor_id: row.instructor?.id || row.instructor_id, instructor_name: row.instructor?.full_name || "Unknown",
        course_id: row.course?.id, course_title: row.course?.title || "Untitled", at: row.published_at,
      })),
    };
  },

  // Get real-time metrics: last-hour learners with lesson activity, signups
  // and completed purchases. A failure is reported as unavailable, not zeros.
  async getRealTimeMetrics(options = {}) {
    const now = new Date();
    try {
      const [excludedUserIds, transactionExclusions] = await Promise.all([
        getAnalyticsTestUserIds(options),
        getAnalyticsTransactionExclusionIds(),
      ]);
      const since = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
      const [activity, signups, purchases] = await Promise.all([
        fetchAllRows(() =>
          excludeAnalyticsUsers(
            supabase
              .from("lesson_progress")
              .select("id, user_id")
              .gte("last_accessed_at", since),
            excludedUserIds,
          ),
        ),
        excludeAnalyticsUsers(
          supabase
            .from("user_profiles")
            .select("id", { count: "exact", head: true })
            .gte("created_at", since),
          excludedUserIds,
          "id",
        ),
        excludeAnalyticsTransactions(
          excludeAnalyticsUsers(
            supabase
              .from("payments")
              .select("id", { count: "exact", head: true })
              .eq("platform", getCurrentPlatform())
              .eq("payment_status", "completed")
              .gte("paid_at", since),
            excludedUserIds,
          ),
          transactionExclusions.paymentIds,
        ),
      ]);
      if (signups.error) throw signups.error;
      if (purchases.error) throw purchases.error;

      return {
        active_users: new Set(activity.map((row) => row.user_id).filter(Boolean))
          .size,
        signups_last_hour: signups.count ?? 0,
        purchases_last_hour: purchases.count ?? 0,
        timestamp: now.toISOString(),
      };
    } catch (error) {
      console.error("Error fetching real-time metrics:", error);
      return {
        active_users: null,
        signups_last_hour: null,
        purchases_last_hour: null,
        unavailable: true,
        timestamp: now.toISOString(),
      };
    }
  },

  // Utility function to get time ago
  getTimeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`;
  },

  // Get recent signups and latest activity
  async getRecentSignupsAndActivity(limit = 10, options = {}) {
    try {
      const excludedUserIds = await getAnalyticsTestUserIds(options);
      // 1. Get recent signups
      let signupsQuery = supabase
        .from("user_profiles")
        .select("id, full_name, email, created_at, role");
      signupsQuery = excludeAnalyticsUsers(signupsQuery, excludedUserIds, "id");
      signupsQuery = signupsQuery
        .order("created_at", { ascending: false })
        .limit(limit);
      const { data: signups, error: signupsError } = await signupsQuery;

      if (signupsError) throw signupsError;

      // 2. Get recent activity from lesson_progress (serving as "logins")
      let activityQuery = supabase
        .from("lesson_progress")
        .select(
          `
          user_id,
          last_accessed_at,
          user:user_profiles(full_name, email)
        `,
        );
      activityQuery = excludeAnalyticsUsers(activityQuery, excludedUserIds);
      activityQuery = activityQuery
        .order("last_accessed_at", { ascending: false })
        .limit(limit);
      const { data: activity, error: activityError } = await activityQuery;

      if (activityError) throw activityError;

      return {
        signups: signups.map((s) => ({
          ...s,
          type: "signup",
          timestamp: s.created_at,
        })),
        activity: activity.map((a) => ({
          user_id: a.user_id,
          full_name: a.user?.full_name,
          email: a.user?.email,
          timestamp: a.last_accessed_at,
          type: "activity",
        })),
      };
    } catch (error) {
      console.error("Error fetching recent signups and activity:", error);
      return { signups: [], activity: [] };
    }
  },

  // Generate analytics report
  async generateReport(reportType = "admin_dashboard", parameters = {}) {
    try {
      let data = {};

      switch (reportType) {
        case "admin_dashboard":
          data = await this.getAdminAnalytics(parameters?.timeframe || "30", {
            excludeTestAccounts: parameters?.excludeTestAccounts ?? true,
            testAccountEmails: parameters?.testAccountEmails,
          });
          break;
        case "student_summary":
          data = await this.getStudentAnalytics(
            parameters?.timeframe || "30",
            {
              excludeTestAccounts: parameters?.excludeTestAccounts ?? true,
              testAccountEmails: parameters?.testAccountEmails,
            },
          );
          break;
        case "course_management":
          data = await this.getCourseManagementAnalytics({
            excludeTestAccounts: parameters?.excludeTestAccounts ?? true,
            testAccountEmails: parameters?.testAccountEmails,
          });
          break;
        case "user_management":
          data = await this.getUserManagementAnalytics({
            excludeTestAccounts: parameters?.excludeTestAccounts ?? true,
            testAccountEmails: parameters?.testAccountEmails,
          });
          break;
        case "enrollment_trends":
          data = await this.getEnrollmentTrends(parameters?.period || "30", {
            excludeTestAccounts: parameters?.excludeTestAccounts ?? true,
            testAccountEmails: parameters?.testAccountEmails,
          });
          break;
        default:
          throw new Error(`Unsupported report type: ${reportType}`);
      }

      return {
        report_type: reportType,
        generated_at: new Date()?.toISOString(),
        parameters,
        data,
      };
    } catch (error) {
      console.error("Error generating report:", error);
      throw new Error("Failed to generate analytics report");
    }
  },
};
